import axios, { AxiosResponse } from 'axios';
import { BASE_URL } from '../../../../config/environmentConfig';
import { identityActionTypes } from '../identity-action-types';
import { errorAlert, successAlert } from '../../../components/ToastMessage';
import { sendSentryMessage } from '../../../utils/sentry-helper';
import * as Sentry from '@sentry/react';
import { IConfirmPasswordFields } from '../../../constants/confirmAccount/confirm-account-form';
import {
	getFormattedChangePasswordFormFields, IChangePasswordFields
} from '../../../constants/profile-settings/account-security/change-password-form';
import * as React from 'react';
import { IAction } from '../identity-interfaces';
import { ITwoFactorAuthParams, IVerificationParams } from '../../../components/profileSettings/AccountSecurityAddKey';
import { IVerifyLoginCodeParams } from '../../../views/login/Login';
import { ProfileSettingsReturnTypes } from '../../../constants/profile-settings/profile-settings-form';
import { POST } from '../../store-helper';

export const fetchSecurityInformation = async (dispatch: React.Dispatch<IAction>, email: string): Promise<void> => {
	try {
		dispatch({ type: identityActionTypes.SET_LOADING_SECURITY_INFORMATION, payload: true });

		const data = await POST({
			pathname: `${BASE_URL.IDENTITY}/users/user`,
			body: { email }
		});

		dispatch({ type: identityActionTypes.FETCH_SECURITY_INFORMATION, payload: data ?? {}});
	} catch (e) {
		Sentry.setTag('error-email', email);
		sendSentryMessage({ name: `POST Fetch user data error`, context: e });
		console.log(e);
		dispatch({ type: identityActionTypes.FETCH_SECURITY_INFORMATION, payload: null });
	}
};

export const setTwoFactorAuthentication = async (dispatch: React.Dispatch<IAction>, params: ITwoFactorAuthParams): Promise<AxiosResponse<any>> => {
	dispatch({ type: identityActionTypes.SET_TWO_FACTOR_ENABLE_LOADING, payload: true });
	try {
		const res = await axios.post(`${BASE_URL.IDENTITY}/identity/update-mfa`, params);
		dispatch({ type: identityActionTypes.SET_TWO_FACTOR_ENABLE_LOADING, payload: false });
		return res;
	} catch (e) {
		sendSentryMessage({ name: `POST Set MFA error`, context: e });
		console.log(e);
		dispatch({ type: identityActionTypes.SET_TWO_FACTOR_ENABLE_LOADING, payload: false });
	}
};

export const removeTwoFactorAuthentication = async (dispatch: React.Dispatch<IAction>, email: string, token: string): Promise<void> => {
	dispatch({ type: identityActionTypes.START_REMOVING_TWO_FACTOR_AUTH_LOADING });
	try {
		const params = {
			token,
			email
		};
		await axios.post(`${BASE_URL.IDENTITY}/identity/disable-mfa`, params);
		await fetchSecurityInformation(dispatch, email);
		dispatch({ type: identityActionTypes.END_REMOVING_TWO_FACTOR_AUTH_LOADING });
		successAlert({
			title: 'Two factor authentication was removed!'
		});
	} catch (e) {
		sendSentryMessage({ name: `POST Remove MFA error`, context: e });
		console.log(e);
		dispatch({ type: identityActionTypes.FETCH_FAIL });
		errorAlert({
			title: 'Failed'
		});
	}
};

export const verifyCodeAuthentication = async (
	dispatch: React.Dispatch<IAction>,
	params: IVerificationParams,
	successModal: () => void,
	errorModal: () => void
): Promise<void> => {
	dispatch({ type: identityActionTypes.START_CODE_VERIFICATION_LOADING });
	try {
		await axios.post(`${BASE_URL.IDENTITY}/identity/verify-mfa`, params);
		successModal();
		dispatch({ type: identityActionTypes.END_CODE_VERIFICATION_LOADING });
	} catch (e) {
		sendSentryMessage({ name: `POST Verify MFA error`, context: e });
		console.log(e);
		errorModal();
		dispatch({ type: identityActionTypes.FETCH_FAIL });
	}
};

export const verifyLoginCodeAuthentication = async (
	dispatch: React.Dispatch<IAction>,
	params: IVerifyLoginCodeParams,
	errorModal: () => void,
	closeCodeModal: () => void,
	handleSuccessLogin: any
): Promise<void> => {
	dispatch({ type: identityActionTypes.START_VERIFICATION_LOGIN_CODE_LOADING });
	try {
		const res = await axios.post(`${BASE_URL.IDENTITY}/identity/signin-challenge`, params);
		dispatch({ type: identityActionTypes.END_VERIFICATION_LOGIN_CODE_LOADING });
		closeCodeModal();
		await handleSuccessLogin(res);
	} catch (e) {
		sendSentryMessage({ name: `POST MFA challenge error`, context: e });
		errorModal();
		dispatch({ type: identityActionTypes.FETCH_FAIL });
	}
};

export const setPersonalInformation = async (params: ProfileSettingsReturnTypes): Promise<void> => {
	await axios.put(`${BASE_URL.IDENTITY}/users/contact-information`, params);
};

export const setNewPassword = async (
	dispatch: React.Dispatch<IAction>,
	formik: any,
	email: string,
	onSuccess: () => void,
	onError: () => void
): Promise<void> => {
	dispatch({ type: identityActionTypes.START_CHANGE_PASSWORD_LOADING });
	try {
		if (!formik.isValid) return;
		const params: IChangePasswordFields = getFormattedChangePasswordFormFields(formik.values);
		params.username = email;
		await axios.post(`${BASE_URL.IDENTITY}/identity/ChangePassword`, params);
		onSuccess();
		dispatch({ type: identityActionTypes.END_CHANGE_PASSWORD_LOADING });
	} catch (e) {
		console.log(e);
		onError();
		dispatch({ type: identityActionTypes.FETCH_FAIL });
	}
};
